import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { Loader2, Wifi, RotateCcw, Camera, Check, X, MapPin, Undo2, Sparkles, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmt = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const STATUS_META = {
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  unknown: { label: 'New — review', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  ignored: { label: 'Ignored', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const SelfieViewer = ({ eventId }) => {
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || !eventId || url) return;
    let revoked = null;
    axios.get(`${API}/api/clock/admin/selfie/${eventId}`, { headers: authHeaders(), responseType: 'blob' })
      .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setUrl(u); })
      .catch(() => toast({ title: 'Selfie unavailable (may have expired)', variant: 'destructive' }));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [open, eventId, url]);

  if (!eventId) return <span className="text-[11px] text-gray-400">No selfie</span>;
  return (
    <div>
      <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setOpen((v) => !v)} data-testid={`ip-selfie-toggle-${eventId}`}>
        <Camera className="h-3.5 w-3.5" /> {open ? 'Hide' : 'View'} selfie
      </Button>
      {open && (
        <div className="mt-2">
          {url ? <img src={url} alt="clock selfie" className="w-28 h-28 object-cover rounded-lg border" data-testid={`ip-selfie-img-${eventId}`} />
            : <div className="w-28 h-28 rounded-lg border flex items-center justify-center text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /></div>}
        </div>
      )}
    </div>
  );
};

const IpReviewTab = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIp, setBusyIp] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [sel, setSel] = useState({}); // ip -> chosen branch_id for approval

  const load = useCallback(async () => {
    try {
      const [r, b] = await Promise.all([
        axios.get(`${API}/api/clock/ip-review`, { headers: authHeaders() }),
        axios.get(`${API}/api/clock/branches`, { headers: authHeaders() }),
      ]);
      setRows(r.data?.rows || []); setCounts(r.data?.counts || {});
      setBranches((b.data || []).filter((x) => x.latitude != null && x.longitude != null));
    } catch { toast({ title: 'Failed to load IP review', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (ip, action, branchId) => {
    setBusyIp(ip);
    try {
      if (action === 'clear') await axios.delete(`${API}/api/clock/ip-rule/${encodeURIComponent(ip)}`, { headers: authHeaders() });
      else await axios.post(`${API}/api/clock/ip-${action}`, { ip, branch_id: branchId || null }, { headers: authHeaders() });
      const label = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : action === 'ignore' ? 'ignored' : 'reset to unreviewed';
      toast({ title: `IP ${ip} ${label}` });
      await load();
    } catch { toast({ title: 'Action failed', variant: 'destructive' }); }
    finally { setBusyIp(null); }
  };

  const autoApprove = async () => {
    setAutoBusy(true);
    try {
      const r = await axios.post(`${API}/api/clock/ip-auto-approve`, {}, { headers: authHeaders() });
      const n = r.data?.approved || 0;
      toast({ title: n ? `Auto-approved ${n} IP(s)` : 'Nothing to auto-approve', description: n ? 'IPs seen only at pinned stations are now trusted.' : 'All unreviewed IPs were also seen at an un-pinned station — review them manually.' });
      await load();
    } catch { toast({ title: 'Auto-approve failed', variant: 'destructive' }); }
    finally { setAutoBusy(false); }
  };

  if (loading) return <div className="py-12 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading IPs…</div>;

  return (
    <div className="space-y-4" data-testid="ip-review-tab">
      <div className="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
        <Wifi className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>Geo-fencing stays as a warning only. Build your trusted boundary here: <b>Approve</b> the IPs that belong to Stars 'N' Bars (optionally pin each to its station so you know the venue), and <b>Reject</b> any outside IP — repeat clock-ins from a rejected IP get flagged &amp; alert HR in real time (never blocked). Use <b>Ignore</b> to dismiss very old / irrelevant IPs from the queue without judging them (never flagged). Approved/rejected/ignored by mistake? Use <b>Undo</b> to revert. Check each selfie before deciding.</span>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Badge variant="outline" className={STATUS_META.unknown.cls}>New / unreviewed: {counts.unknown || 0}</Badge>
        <Badge variant="outline" className={STATUS_META.approved.cls}>Approved: {counts.approved || 0}</Badge>
        <Badge variant="outline" className={STATUS_META.rejected.cls}>Rejected: {counts.rejected || 0}</Badge>
        <Badge variant="outline" className={STATUS_META.ignored.cls}>Ignored: {counts.ignored || 0}</Badge>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 ml-auto border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={autoBusy || (counts.unknown || 0) === 0} onClick={autoApprove} data-testid="ip-auto-approve-btn">
          {autoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Auto-approve pinned-station IPs
        </Button>
      </div>

      {rows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400 text-sm" data-testid="ip-review-empty">No clock-in IPs recorded yet.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.unknown;
            const busy = busyIp === r.ip;
            const chosen = sel[r.ip] || '';
            const showStationPicker = r.status !== 'approved' && branches.length > 0;
            return (
              <Card key={r.ip} data-testid={`ip-row-${r.ip}`}>
                <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold text-gray-900">{r.ip}</span>
                      <Badge variant="outline" className={meta.cls} data-testid={`ip-status-${r.ip}`}>{meta.label}</Badge>
                      <span className="text-xs text-gray-400">{r.count} clock-in(s) · last {fmt(r.last_seen)}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {r.branches?.length ? <>Stations: {r.branches.join(', ')} · </> : null}
                      Staff: {r.employees?.length ? r.employees.slice(0, 8).join(', ') + (r.employees.length > 8 ? ` +${r.employees.length - 8} more` : '') : '—'}
                    </div>
                    {r.status === 'approved' && r.branch_name && (
                      <div className="flex items-center gap-1.5 text-xs text-emerald-700 mt-1" data-testid={`ip-pin-${r.ip}`}>
                        <MapPin className="h-3.5 w-3.5" /> Pinned to <b>{r.branch_name}</b>
                        {r.lat != null && r.lng != null && (
                          <a href={`https://www.google.com/maps?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer" className="underline">
                            {Number(r.lat).toFixed(5)}, {Number(r.lng).toFixed(5)}
                          </a>
                        )}
                      </div>
                    )}
                    {r.updated_by && <div className="text-[11px] text-gray-400 mt-0.5">{meta.label} by {r.updated_by} · {fmt(r.updated_at)}</div>}
                  </div>

                  <SelfieViewer eventId={r.selfie_event_id} />

                  {r.status === 'approved' && r.lat != null && r.lng != null && (
                    <iframe
                      title={`map-${r.ip}`}
                      src={`https://maps.google.com/maps?q=${r.lat},${r.lng}&z=16&output=embed`}
                      className="w-full lg:w-56 h-28 rounded-lg border flex-shrink-0"
                      loading="lazy"
                      data-testid={`ip-map-${r.ip}`}
                    />
                  )}

                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {showStationPicker && (
                      <Select value={chosen} onValueChange={(v) => setSel((p) => ({ ...p, [r.ip]: v }))}>
                        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`ip-station-select-${r.ip}`}><SelectValue placeholder="Pin to station…" /></SelectTrigger>
                        <SelectContent>
                          {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {r.status !== 'approved' && (
                      <Button size="sm" className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(r.ip, 'approve', chosen)} data-testid={`ip-approve-${r.ip}`}>
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve IP
                      </Button>
                    )}
                    {r.status !== 'rejected' && (
                      <Button size="sm" variant="outline" className="h-8 gap-1 border-rose-300 text-rose-700 hover:bg-rose-50" disabled={busy} onClick={() => act(r.ip, 'reject')} data-testid={`ip-reject-${r.ip}`}>
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Reject IP
                      </Button>
                    )}
                    {r.status !== 'ignored' && (
                      <Button size="sm" variant="outline" className="h-8 gap-1 border-gray-300 text-gray-600 hover:bg-gray-100" disabled={busy} title="Ignore — dismiss this IP from the review queue (never flagged)" onClick={() => act(r.ip, 'ignore')} data-testid={`ip-ignore-${r.ip}`}>
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EyeOff className="h-3.5 w-3.5" />} Ignore
                      </Button>
                    )}
                    {r.status !== 'unknown' && (
                      <Button size="sm" variant="ghost" className="h-8 gap-1 text-gray-500 hover:text-gray-800" disabled={busy} title="Undo — revert to unreviewed" onClick={() => act(r.ip, 'clear')} data-testid={`ip-clear-${r.ip}`}>
                        <Undo2 className="h-3.5 w-3.5" /> Undo
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IpReviewTab;
